import { useState, useEffect, useMemo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from "recharts";
import { Sun, Moon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getMovimentosGeral } from "../../api";
import { DARK_THEME_PAG_REC , LIGHT_THEME_PAG_REC } from "../../componets/temas";
import { Pagination } from "../../componets/Pagination";
import { DateRangePicker , ThemeContext} from "../../componets/Calendario";
import SidebarNav from "../../componets/SideBarNav";
// ─── PAGINAÇÃO ────────────────────────────────────────────────────────────────
const ROWS_PER_PAGE = 15;

// ─── CONSTANTES DE NEGÓCIO ───────────────────────────────────────────────────
const GRUPO_VALIDO       = "CONTA_CORRENTE_PAG";
const FLUXO_EXCLUIDO     = "Não Considerada";
const CAMPO_FLUXO        = "Fluxo de Caixa";
const CAMPO_DATA_PAG     = "Data Pagamento";
const CAMPO_DATA_EMISSAO = "Data Emissão";
const CAMPO_VALOR_PAGO   = "Valor Pago";
const CAMPO_PRESTADOR    = "Prestador";
const CAMPO_CAT_SUPERIOR = "Categoria Superior";
const CAMPO_GRUPO        = "Grupo";

const CATEGORIAS_EXCLUIDAS = [
  "Sócios", 
  "Sócio", 
  "Transferència", 
  "Transferência",
  "Movimentação de Recursos", 
  "Distribuição de Lucro", 
  "Outras saídas", 
  "Outras Entradas", 
  "Outras Despesas", 
  "Pagamentos Antecipado", 
  "Devoluções de Vendas", 
  "Balanço", 
  "Despesas Diretas", 
  "Despesas de Vendas e Marketing", 
  "Despesas com Pessoal", 
  "Despesas Administrativas", 
  "Despesas Financeiras / Bancos"
];

// ─── PARSERS ─────────────────────────────────────────────────────────────────
const parseBRL = (v) => {
  if (v == null || v === "") return null;
  if (typeof v === "number") return v;
  const n = parseFloat(String(v).replace(/\./g, "").replace(",", "."));
  return isNaN(n) ? null : n;
};

const parseDate = (v) => {
  if (!v) return null;
  const s = String(v).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
    const [dd, mm, yyyy] = s.split("/");
    const d = new Date(`${yyyy}-${mm}-${dd}`);
    return isNaN(d) ? null : d;
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const d = new Date(s);
    return isNaN(d) ? null : d;
  }
  return null;
};

const dateDiffDays = (dataPag, dataEmissao) => {
  if (!dataPag || !dataEmissao) return null;
  const diff = Math.round((dataPag - dataEmissao) / 86_400_000);
  return diff < 0 ? 0 : diff;
};

const toMonthKey = (d) => {
  if (!d) return null;
  const s = String(d).trim();
  const parts = s.split("/");
  if (parts.length === 3) return `${parts[1]}/${parts[2]}`;
  return null;
};

const monthLabel = (key) => {
  if (!key) return key;
  const [mm, yyyy] = key.split("/");
  const months = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
  const label = months[parseInt(mm, 10) - 1] || mm;
  return yyyy ? `${label} ${yyyy}` : label;
};

const fmt = (v) =>
  v == null ? "-" :
  new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);

const fmtShort = (v) => {
  if (v == null) return "-";
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)} mi`;
  if (Math.abs(v) >= 1_000)     return `${(v / 1_000).toFixed(0)} mil`;
  return fmt(v);
};

const fmtDias = (v) => v == null ? "-" : `${Number(v).toFixed(2)}`;

// ─── UI ATOMS ─────────────────────────────────────────────────────────────────
function Select({ T, label, options, value, onChange }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 200 }}>
      <label style={{
        fontSize: 10, fontWeight: 600, letterSpacing: "0.12em",
        color: T.headerText, textTransform: "uppercase",
      }}>
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          background: T.selectBg,
          color: T.headerText,
          border: `1px solid ${T.selectBorder}`,
          borderRadius: 6, padding: "9px 36px 9px 12px", fontSize: 13,
          fontFamily: "inherit", appearance: "none", cursor: "pointer",
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23ffffff99' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
          backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center",
          outline: "none", minWidth: 200,
          backdropFilter: "blur(4px)",
        }}
      >
        {options.map(o => (
          <option key={o.value} value={o.value} style={{ background: "#1a1a2e", color: "#fff" }}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function KpiCard({ T, label, value, sub, accent = false }) {
  return (
    <div style={{
      background: T.card,
      border: `1px solid ${accent ? T.brand : T.border}`,
      borderRadius: 10, padding: "18px 24px",
      display: "flex", flexDirection: "column", gap: 6,
      boxShadow: accent ? `0 0 28px ${T.brand}33` : T.shadowCard,
      transition: "background 0.3s, border-color 0.3s",
    }}>
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", color: T.muted, textTransform: "uppercase" }}>{label}</span>
      <span style={{ fontSize: 28, fontWeight: 800, color: accent ? T.brand : T.text, fontVariantNumeric: "tabular-nums", lineHeight: 1.1 }}>{value}</span>
      {sub && <span style={{ fontSize: 11, color: T.muted }}>{sub}</span>}
    </div>
  );
}

function SectionTitle({ T, children }) {
  return (
    <h3 style={{ margin: "0 0 18px 0", fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", color: T.muted, textTransform: "uppercase" }}>
      {children}
    </h3>
  );
}

function Spinner({ T }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 320, gap: 16 }}>
      <div style={{
        width: 40, height: 40,
        border: `3px solid ${T.border}`,
        borderTop: `3px solid ${T.brand}`,
        borderRadius: "50%",
        animation: "spin 0.8s linear infinite",
      }} />
      <span style={{ color: T.muted, fontSize: 13 }}>Carregando dados…</span>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function ErrorBanner({ T, message, onRetry }) {
  return (
    <div style={{
      background: `${T.neg}18`,
      border: `1px solid ${T.neg}55`,
      borderRadius: 10, padding: "14px 20px",
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginBottom: 24,
    }}>
      <span style={{ color: T.neg, fontSize: 13 }}>⚠️ {message}</span>
      <button
        onClick={onRetry}
        style={{ background: T.brand, color: "#fff", border: "none", borderRadius: 6, padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
      >
        Tentar novamente
      </button>
    </div>
  );
}

function TooltipPrazo({ T, active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: T.tooltipBg, border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 14px", boxShadow: T.shadow }}>
      <p style={{ margin: "0 0 6px 0", fontSize: 11, color: T.muted }}>{label}</p>
      {payload.map(p => (
        <p key={p.name} style={{ margin: "2px 0", fontSize: 13, color: p.color, fontWeight: 600 }}>
          {p.name}: {Number(p.value).toFixed(2)} dias
        </p>
      ))}
    </div>
  );
}

function TooltipSaidas({ T, active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: T.tooltipBg, border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 14px", boxShadow: T.shadow }}>
      <p style={{ margin: "0 0 6px 0", fontSize: 11, color: T.muted }}>{label}</p>
      {payload.map(p => (
        <p key={p.name} style={{ margin: "2px 0", fontSize: 13, color: p.color, fontWeight: 600 }}>
          {p.name}: R$ {fmtShort(p.value)}
        </p>
      ))}
    </div>
  );
}


function ThemeToggle({ isLight, onToggle }) {
  return (
    <button
      onClick={onToggle}
      title={isLight ? "Mudar para tema claro" : "Mudar para tema escuro"}
      style={{
        display: "flex", alignItems: "center", gap: 8,
        background: "rgba(255,255,255,0.12)",
        border: "1px solid rgba(255,255,255,0.2)",
        borderRadius: 8, padding: "7px 14px",
        color: "#fff", fontSize: 12, fontWeight: 600,
        cursor: "pointer", fontFamily: "inherit",
        transition: "background 0.2s",
        backdropFilter: "blur(4px)",
      }}
      onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.2)"}
      onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.12)"}
    >
      {isLight ? <><Sun size={14} /> Claro</> : <><Moon size={14} /> Escuro</>}
    </button>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function PrazoPagamentoDash() {
  const [dadosRaw, setDadosRaw] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [erro,     setErro]     = useState(null);
  const [isLight,   setisLight]   = useState(true);
  const [dateRange,       setDateRange]       = useState({
    start: new Date("2025-01-01T00:00:00"),
    end:   new Date("2026-12-31T00:00:00"),
  });
  const T = isLight ? LIGHT_THEME_PAG_REC : DARK_THEME_PAG_REC ;

  // Paginação
  const [paginaGrupo, setPaginaGrupo] = useState(1);

  // Filtros
  const [filtPrestador, setFiltPrestador] = useState("all");
  const [filtCategoria, setFiltCategoria] = useState("all");


  const navigate = useNavigate();

  const getDataAtualizacao = () => {
    const agora = new Date();
    if (agora.getHours() < 12) agora.setDate(agora.getDate() - 1);
    return `${agora.toLocaleDateString("pt-BR")} - 12:00`;
  };

  const fetchDados = () => {
    setLoading(true);
    setErro(null);
    getMovimentosGeral()
      .then(res => {
        const data = Array.isArray(res) ? res : res.data || [];
        setDadosRaw(data);
        setLoading(false);
      })
      .catch(err => {
        setErro(err.message);
        setLoading(false);
      });
  };

  useEffect(() => { fetchDados(); }, []);

  // ── Dados base ────────────────────────────────────────────────────────────
  const dadosBase = useMemo(() =>
    dadosRaw
      .filter(m => {
        if (!parseDate(m[CAMPO_DATA_PAG]))      return false;
        if (m[CAMPO_FLUXO] === FLUXO_EXCLUIDO) return false;
        if (m[CAMPO_GRUPO] !== GRUPO_VALIDO)    return false;
        if (CATEGORIAS_EXCLUIDAS.includes(m[CAMPO_CAT_SUPERIOR])) return false;
        return true;
      })
      .map(m => {
        const dp  = parseDate(m[CAMPO_DATA_PAG]);
        const de  = parseDate(m[CAMPO_DATA_EMISSAO]);
        const val = parseBRL(m[CAMPO_VALOR_PAGO]) ?? 0;
        const diffDias = (dp && de) ? dateDiffDays(dp, de) ?? 0 : 0;
        return { ...m, _dp: dp, _diffDias: diffDias, _valorPago: val };
      }),
    [dadosRaw]
  );

  // ── Opções de filtros ─────────────────────────────────────────────────────
  const prestadorOpcoes = useMemo(() => {
    const u = [...new Set(dadosBase.map(r => r[CAMPO_PRESTADOR]).filter(Boolean))].sort();
    return [{ value: "all", label: "Todos os Prestadores" }, ...u.map(v => ({ value: v, label: v }))];
  }, [dadosBase]);

  const categoriaOpcoes = useMemo(() => {
    const u = [...new Set(dadosBase.map(r => r[CAMPO_CAT_SUPERIOR]).filter(Boolean))].sort();
    return [{ value: "all", label: "Todas as Categorias" }, ...u.map(v => ({ value: v, label: v }))];
  }, [dadosBase]);

  // ── Filtros aplicados ─────────────────────────────────────────────────────
  const dadosFiltrados = useMemo(() => {
      setPaginaGrupo(1);
      return dadosBase.filter(r => {
        // Filtros de texto
        if (filtPrestador !== "all" && r[CAMPO_PRESTADOR]    !== filtPrestador) return false;
        if (filtCategoria !== "all" && r[CAMPO_CAT_SUPERIOR] !== filtCategoria) return false;
        
        // Filtro de Data (Range)
        const dataPag = r._dp;
        if (dataPag < dateRange.start || dataPag > dateRange.end) return false;

        return true;
      });
    }, [dadosBase, filtPrestador, filtCategoria, dateRange]);

  // ── KPIs ──────────────────────────────────────────────────────────────────
  const prazoMedioTotal = useMemo(() => {
    const sumProd  = dadosFiltrados.reduce((s, r) => s + (r._diffDias * r._valorPago), 0);
    const sumValor = dadosFiltrados.reduce((s, r) => s + r._valorPago, 0);
    return sumValor > 0 ? sumProd / sumValor : null;
  }, [dadosFiltrados]);

  const totalValorPago = useMemo(() =>
    dadosFiltrados.reduce((s, r) => s + r._valorPago, 0),
    [dadosFiltrados]
  );

  // ── Tabela: por Categoria Superior ───────────────────────────────────────
  const tabelaGrupos = useMemo(() => {
    const map = {};
    dadosFiltrados.forEach(r => {
      const grupo = r[CAMPO_CAT_SUPERIOR] || "Outros";
      if (!map[grupo]) map[grupo] = { sumProd: 0, sumValor: 0 };
      map[grupo].sumProd  += r._diffDias * r._valorPago;
      map[grupo].sumValor += r._valorPago;
    });
    return Object.entries(map)
      .map(([grupo, { sumProd, sumValor }]) => ({
        grupo,
        prazoMedio: sumValor > 0 ? sumProd / sumValor : null,
        valorPago:  sumValor,
      }))
      .sort((a, b) => (b.valorPago || 0) - (a.valorPago || 0));
  }, [dadosFiltrados]);

  // ── Paginação ─────────────────────────────────────────────────────────────
  const gruposPaginados = useMemo(() => {
    const inicio = (paginaGrupo - 1) * ROWS_PER_PAGE;
    return tabelaGrupos.slice(inicio, inicio + ROWS_PER_PAGE);
  }, [tabelaGrupos, paginaGrupo]);

  // ── Gráfico 1: Prazo Médio por Mês ───────────────────────────────────────
  const graficoPrazo = useMemo(() => {
    const map = {};
    dadosFiltrados.forEach(r => {
      const mk = toMonthKey(r[CAMPO_DATA_PAG]);
      if (!mk) return;
      if (!map[mk]) map[mk] = { sumProd: 0, sumValor: 0 };
      map[mk].sumProd  += r._diffDias * r._valorPago;
      map[mk].sumValor += r._valorPago;
    });
    return Object.entries(map)
      .sort((a, b) => {
        const [ma, ya] = a[0].split("/").map(Number);
        const [mb, yb] = b[0].split("/").map(Number);
        return ya !== yb ? ya - yb : ma - mb;
      })
      .map(([mk, { sumProd, sumValor }]) => ({
        mes: monthLabel(mk),
        "Prazo Médio": sumValor > 0 ? parseFloat((sumProd / sumValor).toFixed(2)) : null,
      }));
  }, [dadosFiltrados]);

  // ── Gráfico 2: Saídas por Mês ─────────────────────────────────────────────
  const graficoSaidas = useMemo(() => {
    const map = {};
    dadosFiltrados.forEach(r => {
      const mk = toMonthKey(r[CAMPO_DATA_PAG]);
      if (!mk) return;
      map[mk] = (map[mk] || 0) + r._valorPago;
    });
    return Object.entries(map)
      .sort((a, b) => {
        const [ma, ya] = a[0].split("/").map(Number);
        const [mb, yb] = b[0].split("/").map(Number);
        return ya !== yb ? ya - yb : ma - mb;
      })
      .map(([mk, total]) => ({
        mes: monthLabel(mk),
        "Valor Pago": parseFloat(total.toFixed(2)),
      }));
  }, [dadosFiltrados]);

  const isInitialLoad = loading && dadosRaw.length === 0;

  // ─── RENDER ───────────────────────────────────────────────────────────────
return (
  // 1. Raiz: flex row
  <div style={{
    display: "flex",
    height: "100vh",
    overflow: "hidden",
    background: T.bg,
    fontFamily: "'DM Sans','Segoe UI',sans-serif",
    color: T.text,
    transition: "background 0.3s, color 0.3s",
  }}>

    {/* 2. Sidebar — coluna esquerda */}
    <SidebarNav isLight={isLight} />

    {/* 3. Coluna direita — flex column */}
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

      {/* 4. Header — altura fixa, nunca rola */}
      <header style={{
        flexShrink: 0,
        background: T.headerGradient, color: "#fff", padding: "16px 24px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        flexWrap: "wrap", gap: 12, boxShadow: T.shadow,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
            <span style={{ fontSize: 18, fontWeight: 300, color: T.headerText }}>GROUP</span>
          </div>
          <div style={{ fontSize: 10, color: T.headerText, textTransform: "uppercase" }}>
            Atualizado em {getDataAtualizacao()}
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <Select T={T} label="Prestador de Serviço" options={prestadorOpcoes} value={filtPrestador} onChange={setFiltPrestador} />
          <Select T={T} label="Nome Categoria Sup."  options={categoriaOpcoes} value={filtCategoria} onChange={setFiltCategoria} />
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 10, fontWeight: 600, color: T.headerText, textTransform: "uppercase", letterSpacing: "0.12em" }}>
              Período de Pagamento
            </label>
            <ThemeContext.Provider value={T}>
              <DateRangePicker value={dateRange} onChange={setDateRange} />
            </ThemeContext.Provider>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <ThemeToggle isLight={isLight} onToggle={() => setisLight(d => !d)} />
          <button onClick={() => navigate("/inicio")} className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-lg text-sm font-bold">
            Menu Principal
          </button>
        </div>
      </header>

      {/* 5. Área de scroll — ocupa o restante da altura */}
      <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
        <div style={{ padding: "32px 40px", maxWidth: 1400, margin: "0 auto" }}>

          {erro && <ErrorBanner T={T} message={erro} onRetry={fetchDados} />}

          {isInitialLoad ? <Spinner T={T} /> : (
            <>
              <div style={{ marginBottom: 28 }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: T.text, letterSpacing: "-0.01em" }}>
                  Indicadores — Prazo de Pagamento
                </h2>
                <p style={{ margin: "4px 0 0", fontSize: 12, color: T.muted }}>
                  Movimentos Geral · Grupo: {GRUPO_VALIDO} · {dadosFiltrados.length} registros
                </p>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginBottom: 36 }}>
                <KpiCard T={T} label="Prazo Médio de Pagamento Total" value={prazoMedioTotal != null ? `${fmtDias(prazoMedioTotal)} dias` : "-"} sub="Média ponderada pelo Valor Pago" accent />
                <KpiCard T={T} label="Total Valor Pago" value={`R$ ${fmtShort(totalValorPago)}`} sub={`R$ ${fmt(totalValorPago)}`} />
                <KpiCard T={T} label="Grupos de Gastos" value={tabelaGrupos.length} sub="com lançamentos no período" />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, alignItems: "start" }}>

                {/* Tabela */}
                <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden", boxShadow: T.shadowCard }}>
                  <div style={{ padding: "20px 20px 14px", background: T.surface }}>
                    <SectionTitle T={T}>Detalhamento por Grupo de Gastos</SectionTitle>
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${T.border}`, background: T.surface }}>
                        {["Grupo de Gastos", "Prazo Médio", "Valor (R$)"].map((col, i) => (
                          <th key={col} style={{ padding: "11px 16px", textAlign: i === 0 ? "left" : "right", fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", color: T.muted, textTransform: "uppercase" }}>
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {gruposPaginados.length === 0 ? (
                        <tr><td colSpan={3} style={{ padding: "40px 20px", textAlign: "center", color: T.muted }}>Nenhum dado para os filtros selecionados.</td></tr>
                      ) : (
                        <>
                          {gruposPaginados.map((row, i) => (
                            <tr key={row.grupo}
                              style={{ borderBottom: `1px solid ${T.border}`, background: i % 2 === 0 ? "transparent" : `${T.surface}88` }}
                              onMouseEnter={e => e.currentTarget.style.background = `${T.brand}18`}
                              onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? "transparent" : `${T.surface}88`}
                            >
                              <td style={{ padding: "10px 16px", color: T.text, fontWeight: 500 }}>{row.grupo}</td>
                              <td style={{ padding: "10px 16px", textAlign: "right", color: T.line2, fontWeight: 700 }}>{row.prazoMedio != null ? fmtDias(row.prazoMedio) : "-"}</td>
                              <td style={{ padding: "10px 16px", textAlign: "right", color: T.text }}>{fmt(row.valorPago)}</td>
                            </tr>
                          ))}
                          {paginaGrupo === Math.ceil(tabelaGrupos.length / ROWS_PER_PAGE) && (
                            <tr style={{ borderTop: `2px solid ${T.border}`, background: T.surface }}>
                              <td style={{ padding: "12px 16px", fontWeight: 800, color: T.text }}>Total geral</td>
                              <td style={{ padding: "12px 16px", textAlign: "right", fontWeight: 800, color: T.brand }}>{prazoMedioTotal != null ? fmtDias(prazoMedioTotal) : "-"}</td>
                              <td style={{ padding: "12px 16px", textAlign: "right", fontWeight: 800, color: T.text }}>{fmt(totalValorPago)}</td>
                            </tr>
                          )}
                        </>
                      )}
                    </tbody>
                  </table>
                  <Pagination T={T} total={tabelaGrupos.length} page={paginaGrupo} perPage={ROWS_PER_PAGE} onChange={setPaginaGrupo} />
                </div>

                {/* Gráficos */}
                <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                  <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: "20px 20px 12px", boxShadow: T.shadowCard }}>
                    <SectionTitle T={T}>Prazo Médio de Pagamento (Mês)</SectionTitle>
                    {graficoPrazo.length === 0 ? (
                      <div style={{ textAlign: "center", padding: 48, color: T.muted }}>Sem dados.</div>
                    ) : (
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={graficoPrazo} margin={{ top: 4, right: 8, left: 0, bottom: 60 }} barCategoryGap="30%">
                          <CartesianGrid vertical={false} stroke={T.border} />
                          <XAxis dataKey="mes" tick={{ fill: T.muted, fontSize: 10 }} axisLine={false} tickLine={false} angle={-35} textAnchor="end" interval={0} />
                          <YAxis tick={{ fill: T.muted, fontSize: 10 }} axisLine={false} tickLine={false} width={36} tickFormatter={v => `${v}d`} />
                          {prazoMedioTotal != null && (
                            <ReferenceLine y={prazoMedioTotal} stroke={T.line2} strokeDasharray="5 4"
                              label={{ value: `Média ${fmtDias(prazoMedioTotal)}d`, fill: T.line2, fontSize: 10, position: "insideTopRight" }} />
                          )}
                          <Tooltip content={(props) => <TooltipPrazo T={T} {...props} />} cursor={{ fill: `${T.brand}11` }} />
                          <Bar dataKey="Prazo Médio" fill={T.brand} radius={[3, 3, 0, 0]} maxBarSize={48} />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>

                  <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: "20px 20px 12px", boxShadow: T.shadowCard }}>
                    <SectionTitle T={T}>Saídas Mês (R$)</SectionTitle>
                    {graficoSaidas.length === 0 ? (
                      <div style={{ textAlign: "center", padding: 48, color: T.muted }}>Sem dados.</div>
                    ) : (
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={graficoSaidas} margin={{ top: 4, right: 8, left: 0, bottom: 60 }} barCategoryGap="30%">
                          <CartesianGrid vertical={false} stroke={T.border} />
                          <XAxis dataKey="mes" tick={{ fill: T.muted, fontSize: 10 }} axisLine={false} tickLine={false} angle={-35} textAnchor="end" interval={0} />
                          <YAxis tick={{ fill: T.muted, fontSize: 10 }} axisLine={false} tickLine={false} width={52} tickFormatter={fmtShort} />
                          <Tooltip content={(props) => <TooltipSaidas T={T} {...props} />} cursor={{ fill: `${T.neg}11` }} />
                          <Bar dataKey="Valor Pago" fill={T.neg} radius={[3, 3, 0, 0]} maxBarSize={48} />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>
              </div>

              <p style={{ marginTop: 16, fontSize: 11, color: T.muted, textAlign: "right" }}>
                {dadosFiltrados.length} registros filtrados · {dadosBase.length} após regras base · {dadosRaw.length} total
              </p>
            </>
          )}

        </div>
      </div>

    </div>
  </div>
);
}